import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Select } from "@package/subframe/components/Select";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherMoreHorizontal,
} from "@subframe/core";
import { useEffect, useState } from "react";

import {
  clampPage,
  getPaginationRange,
  getShowingRange,
  PAGE_SIZE_OPTIONS,
  totalPages,
} from "@/lib/pagination";
import { cn } from "@/lib/utils";

export interface PaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  itemNoun?: string;
  pageSizeOptions?: readonly number[];
  siblingCount?: number;
  boundaryCount?: number;
  showPageSizeSelect?: boolean;
  showGoToPage?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Controlled pagination footer for list/table screens: a "Showing X to Y of Z
 * {noun}" summary, a page-size dropdown, a go-to-page input, and a constant-width
 * numbered pager with prev/next chevrons. Presentational only — the consumer owns
 * `page` (1-based) / `pageSize` and maps them to the backend's `limit`/`offset`.
 *
 * `onPageSizeChange` is optional (omit to hide the size dropdown); the consumer
 * resets to page 1 on a size change. `itemNoun` is used verbatim, default
 * "results". `siblingCount`/`boundaryCount` tune how many numbers appear around
 * the current page and pinned at each end.
 */
export function Pagination({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  itemNoun = "results",
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  siblingCount = 1,
  boundaryCount = 1,
  showPageSizeSelect = true,
  showGoToPage = true,
  disabled = false,
  className,
}: PaginationProps) {
  const pageCount = totalPages(totalCount, pageSize);
  const currentPage = clampPage(page, pageCount);
  const { start, end, total } = getShowingRange(
    currentPage,
    pageSize,
    totalCount,
  );
  const tokens = getPaginationRange({
    page: currentPage,
    totalPages: pageCount,
    siblingCount,
    boundaryCount,
  });

  const [goToValue, setGoToValue] = useState(String(currentPage));
  // Keep the input in sync when the page changes via any other control.
  useEffect(() => {
    setGoToValue(String(currentPage));
  }, [currentPage]);

  const commitGoTo = () => {
    const parsed = Number.parseInt(goToValue, 10);
    if (Number.isNaN(parsed)) {
      setGoToValue(String(currentPage));
      return;
    }
    const next = clampPage(parsed, pageCount);
    setGoToValue(String(next));
    if (next !== currentPage) {
      onPageChange(next);
    }
  };

  const showPager = pageCount > 1;

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        "flex w-full flex-wrap items-center justify-between gap-4",
        className,
      )}
    >
      <span className="text-body font-body text-subtext-color">
        Showing {start} to {end} of {total} {itemNoun}
      </span>

      <div className="flex items-center gap-4">
        {showPageSizeSelect && onPageSizeChange ? (
          <div className="flex items-center gap-2">
            <span className="whitespace-nowrap text-body font-body text-subtext-color">
              Show
            </span>
            <Select
              className="w-20"
              value={String(pageSize)}
              onValueChange={(value) => onPageSizeChange(Number(value))}
              disabled={disabled}
            >
              {pageSizeOptions.map((option) => (
                <Select.Item key={option} value={String(option)}>
                  {String(option)}
                </Select.Item>
              ))}
            </Select>
          </div>
        ) : null}

        {showGoToPage && showPager ? (
          <div className="hidden items-center gap-2 sm:flex">
            <span className="whitespace-nowrap text-body font-body text-subtext-color">
              Go to
            </span>
            <TextField className="w-16" disabled={disabled}>
              <TextField.Input
                aria-label="Go to page"
                inputMode="numeric"
                disabled={disabled}
                value={goToValue}
                onChange={(event) => setGoToValue(event.target.value)}
                onBlur={commitGoTo}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitGoTo();
                  }
                }}
              />
            </TextField>
          </div>
        ) : null}

        {showPager ? (
          <div className="flex items-center gap-1">
            <IconButton
              variant="neutral-secondary"
              icon={<FeatherChevronLeft />}
              aria-label="Previous page"
              disabled={disabled || currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            />
            <div className="hidden items-center gap-1 sm:flex">
              {tokens.map((token) =>
                typeof token === "number" ? (
                  <Button
                    key={token}
                    variant={
                      token === currentPage
                        ? "brand-primary"
                        : "neutral-secondary"
                    }
                    className="min-w-8 justify-center px-2"
                    aria-current={token === currentPage ? "page" : undefined}
                    aria-label={`Page ${token}`}
                    disabled={disabled}
                    onClick={() => onPageChange(token)}
                  >
                    {String(token)}
                  </Button>
                ) : (
                  <span
                    key={token}
                    aria-hidden
                    className="flex h-8 w-8 items-center justify-center text-subtext-color"
                  >
                    <FeatherMoreHorizontal className="text-body font-body" />
                  </span>
                ),
              )}
            </div>
            <IconButton
              variant="neutral-secondary"
              icon={<FeatherChevronRight />}
              aria-label="Next page"
              disabled={disabled || currentPage >= pageCount}
              onClick={() => onPageChange(currentPage + 1)}
            />
          </div>
        ) : null}
      </div>
    </nav>
  );
}
